import { Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AddressDetail, AddressLookupService, AddressSuggestion } from '@raptor-fe/services';
import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, of, pipe, switchMap, tap } from 'rxjs';

/**
 * A reusable address typeahead: the customer types, picks a suggestion, and the resolved address is emitted.
 *
 * @input **tenantId** - The tenant whose configured lookup provider answers.
 * @input **value** - The text shown in the field.
 * @input **regionCode** - Biases suggestions to a country, when known.
 * @input **disabled** - Blocks typing and lookups.
 * @input **invalid** - Marks the field failed validation.
 * @input **inputId** - Ties an external label to the input.
 * @output **valueChange** - The customer typed.
 * @output **blurred** - The field lost focus.
 * @output **addressSelected** - The customer picked a suggestion, resolved to its full address.
 */
@Component({
    selector: 'lib-address-lookup',
    imports: [FormsModule],
    templateUrl: './address-lookup.component.html',
    styleUrl: './address-lookup.component.scss',
})
export class AddressLookupComponent {
    private readonly lookup = inject(AddressLookupService);

    readonly tenantId = input.required<string>();

    readonly value = input<string>('');

    readonly regionCode = input<string | undefined>(undefined);

    readonly disabled = input<boolean>(false);

    readonly invalid = input<boolean>(false);

    readonly placeholder = input<string>('');

    readonly inputId = input<string | undefined>(undefined);

    readonly valueChange = output<string>();

    readonly blurred = output<void>();

    readonly addressSelected = output<AddressDetail>();

    protected readonly suggestions = signal<AddressSuggestion[]>([]);

    private sessionToken = '';

    private readonly search = rxMethod<string>(
        pipe(
            debounceTime(250),
            distinctUntilChanged(),
            switchMap((term) =>
                term.length < 3
                    ? of<AddressSuggestion[]>([])
                    : this.lookup
                          .autocomplete(this.tenantId(), term, this.ensureToken(), this.regionCode())
                          .pipe(catchError(() => of<AddressSuggestion[]>([]))),
            ),
            tap((results) => this.suggestions.set(results ?? [])),
        ),
    );

    protected onInput(value: string): void {
        this.valueChange.emit(value);
        this.search(value.trim());
    }

    protected async onPick(suggestionId: string): Promise<void> {
        const detail = await firstValueFrom(this.lookup.getDetail(this.tenantId(), suggestionId, this.sessionToken)).catch(() => null);

        this.suggestions.set([]);
        this.sessionToken = '';

        if (detail) {
            this.addressSelected.emit(detail);
        }
    }

    private ensureToken(): string {
        if (!this.sessionToken) {
            this.sessionToken = crypto.randomUUID();
        }

        return this.sessionToken;
    }
}
